import React, { CSSProperties, useMemo, useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { CustomerIntelligenceToolbar } from "@/features/orders-analytics/components/customer-intelligence/CustomerIntelligenceToolbar";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { KpiCard } from "@/shell/components/KpiCard";
import { CohortTriangle, CohortRow, CohortMode } from "@/shell/components/CohortTriangle";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { useAvailableClients } from "@/features/orders-analytics/hooks/use-available-clients";
import { useCustomerIntelligence } from "@/features/orders-analytics/hooks/use-customer-intelligence";
import { FrequencyTiersSection } from "@/features/orders-analytics/components/customer-intelligence/FrequencyTiersSection";
import { RfmSegmentsSection } from "@/features/orders-analytics/components/customer-intelligence/RfmSegmentsSection";
import { ValueConcentrationSection } from "@/features/orders-analytics/components/customer-intelligence/ValueConcentrationSection";
import { CounterpartiesSection } from "@/features/orders-analytics/components/customer-intelligence/CounterpartiesSection";
import { SenderProfileSection } from "@/features/orders-analytics/components/customer-intelligence/SenderProfileSection";
import { EntityIntelligenceSection } from "@/features/orders-analytics/components/customer-intelligence/EntityIntelligenceSection";
import { LifecycleSection } from "@/features/orders-analytics/components/customer-intelligence/LifecycleSection";
import { AudienceExportSection } from "@/features/orders-analytics/components/customer-intelligence/AudienceExportSection";
import { useContactsIntelligence } from "@/features/orders-analytics/hooks/use-contacts-intelligence";
import { useSenderProfile } from "@/features/orders-analytics/hooks/use-sender-profile";
import { useEntityIntelligence } from "@/features/orders-analytics/hooks/use-entity-intelligence";
import { formatters } from "@/features/orders-analytics/utils/formatters";
import { CustomerIntelligenceResponse, OperationType } from "@/features/orders-analytics/types/orders-analytics.types";
import "@/features/orders-analytics/styles/customer-intelligence.css";

type Headline = {
    icon: string;
    label: string;
    value: string;
    sub: string;
    tint: "primary" | "violet" | "success" | "warning";
};

const buildHeadlines = (data: CustomerIntelligenceResponse): Headline[] => {
    const singleOrderTier = data.frequencyTiers.find((tier) => tier.label === "1");
    const repeatAccounts =
        data.totalAccounts - (singleOrderTier !== undefined ? singleOrderTier.accounts : 0);
    const repeatRate =
        data.totalAccounts > 0 ? (repeatAccounts / data.totalAccounts) * 100 : 0;
    const champions = data.rfm.find((segment) => segment.segment === "Champions");
    const lastMonth =
        data.lifecycle.length > 0 ? data.lifecycle[data.lifecycle.length - 1] : null;

    const headlines: Headline[] = [
        {
            icon: "groups",
            label: "Active senders",
            value: formatters.formatNumber(data.totalAccounts),
            sub: "accounts with ≥1 order in range",
            tint: "primary",
        },
        {
            icon: "payments",
            label: "Business revenue",
            value: formatters.formatCurrency(data.totalRevenue),
            sub: "completed orders in range",
            tint: "violet",
        },
        {
            icon: "account_balance_wallet",
            label: "Revenue / account",
            value: formatters.formatCurrency(data.revenuePerAccount),
            sub: "average across senders",
            tint: "violet",
        },
        {
            icon: "shopping_cart",
            label: "Orders / account",
            value: formatters.formatNumber(data.ordersPerAccount),
            sub: "average across senders",
            tint: "violet",
        },
        {
            icon: "repeat",
            label: "Repeat rate",
            value: formatters.formatPercentage(repeatRate),
            sub: `${formatters.formatNumber(repeatAccounts)} accounts with 2+ orders`,
            tint: "success",
        },
        {
            icon: "emoji_events",
            label: "Champions revenue",
            value: formatters.formatPercentage(champions !== undefined ? champions.revenueShare : 0),
            sub:
                champions !== undefined
                    ? `${formatters.formatNumber(champions.accounts)} accounts`
                    : "no champions in range",
            tint: "success",
        },
    ];

    if (lastMonth !== null) {
        headlines.push(
            {
                icon: "person_add",
                label: `New (${lastMonth.label})`,
                value: formatters.formatNumber(lastMonth.newAccounts),
                sub: "first order this month",
                tint: "primary",
            },
            {
                icon: "autorenew",
                label: `Returning (${lastMonth.label})`,
                value: formatters.formatNumber(lastMonth.returningAccounts),
                sub: `reactivated ${formatters.formatNumber(lastMonth.reactivatedAccounts)}`,
                tint: "success",
            }
        );
    }

    return headlines;
};

const toCohortRows = (data: CustomerIntelligenceResponse): CohortRow[] =>
    data.cohorts.rows.map((row) => ({
        cohort: `${row.cohortLabel} · ${formatters.formatNumber(row.size)}`,
        values: row.retention,
        counts: row.activeCounts,
    }));

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_HORIZON_MONTHS = 12;

export function CustomerIntelligencePage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const [horizonMonths, setHorizonMonths] = useState(DEFAULT_HORIZON_MONTHS);
    const [cohortMode, setCohortMode] = useState<CohortMode>("percent");

    // Loyalty/RFM/cohorts need a long horizon, not the dashboard's month-to-date
    // window — so the analysis range is driven by this page's horizon selector,
    // independent of the global date filter. Client scope still comes from filters.
    const analysisFilters = useMemo(() => {
        // Customer-facing universe: include deposits, not just orders/remittances.
        // Each client only deposits OR only orders, so this both surfaces
        // deposit-only clients and keeps "all clients" as the full end-user base
        // (otherwise an order-heavy client like enviaDinero dominates and "all"
        // looks identical to it while hiding every depositor).
        const operationType: OperationType[] = ["order", "deposit"];
        return {
            ...filters,
            operationType,
            dateFrom: Date.now() - horizonMonths * MONTH_MS,
            dateTo: Date.now(),
        };
    }, [filters, horizonMonths]);

    const { availableClients, loading: clientsLoading, refresh: refreshClients } =
        useAvailableClients(analysisFilters);
    const {
        data,
        loading,
        error,
        refresh,
        exportAudience,
        saveAudience,
        clearAudience,
        audienceLoading,
        audienceError,
        audienceCount,
        audienceReady,
    } = useCustomerIntelligence(analysisFilters);
    const { data: contacts } = useContactsIntelligence(analysisFilters);
    const { data: senderProfile } = useSenderProfile(analysisFilters);
    const { data: entityData } = useEntityIntelligence(analysisFilters);

    const headlines = useMemo(() => (data === null ? [] : buildHeadlines(data)), [data]);
    const cohortRows = useMemo(() => (data === null ? [] : toCohortRows(data)), [data]);

    const onRefresh = () => {
        refreshClients();
        refresh();
    };

    const onClientChange = (client: string) => {
        setFilters({ ...filters, clients: client === "" ? [] : [client] });
    };

    return (
        <Shell title="Customer intelligence" subtitle="Loyalty cohorts · RFM · value">
            <div className="ci-page">
                <PageHeader
                    title="Customer intelligence"
                    lede="Loyalty cohorts, order-frequency tiers, RFM segments, value concentration and lifecycle — per account, from order activity."
                    right={
                        <CustomerIntelligenceToolbar
                            availableClients={availableClients}
                            selectedClient={filters.clients[0] ?? ""}
                            onClientChange={onClientChange}
                            horizonMonths={horizonMonths}
                            onHorizonChange={setHorizonMonths}
                            onRefresh={onRefresh}
                            loading={loading || clientsLoading}
                        />
                    }
                />

                {error !== null ? (
                    <div className="card ci-empty" role="alert">
                        No se pudo cargar customer intelligence: {error}
                    </div>
                ) : data === null ? (
                    <div className="card ci-empty" aria-live="polite">
                        {loading ? "Cargando…" : "Sin datos en el rango seleccionado."}
                    </div>
                ) : (
                    <>
                        <section className="section">
                            <SectionHeader title="Overview" meta="account base · value · loyalty" />
                            <div className="grid-5">
                                {headlines.map((kpi) => (
                                    <KpiCard
                                        key={kpi.label}
                                        icon={kpi.icon}
                                        label={kpi.label}
                                        value={kpi.value}
                                        sub={kpi.sub}
                                        tint={kpi.tint}
                                    />
                                ))}
                            </div>
                        </section>

                        {entityData !== null ? (
                            <EntityIntelligenceSection data={entityData} />
                        ) : null}

                        {senderProfile !== null ? (
                            <SenderProfileSection data={senderProfile} />
                        ) : null}

                        <section className="section">
                            <SectionHeader
                                title="Loyalty cohort retention"
                                meta={
                                    cohortMode === "percent"
                                        ? "% of accounts still ordering N months after their first order"
                                        : "accounts still ordering N months after their first order"
                                }
                                right={
                                    <div
                                        className="ci-toggle"
                                        role="group"
                                        aria-label="Cohort value mode"
                                    >
                                        <button
                                            type="button"
                                            aria-pressed={cohortMode === "percent"}
                                            onClick={() => setCohortMode("percent")}
                                            className={
                                                cohortMode === "percent"
                                                    ? "filter filter--active"
                                                    : "filter"
                                            }
                                        >
                                            <span className="v">%</span>
                                        </button>
                                        <button
                                            type="button"
                                            aria-pressed={cohortMode === "absolute"}
                                            onClick={() => setCohortMode("absolute")}
                                            className={
                                                cohortMode === "absolute"
                                                    ? "filter filter--active"
                                                    : "filter"
                                            }
                                        >
                                            <span className="v">#</span>
                                        </button>
                                    </div>
                                }
                            />
                            <div className="ci-legend">
                                Cómo leer la tabla:
                                <ul>
                                    <li>
                                        <b>Cada fila</b> es una cohorte: las cuentas cuyo{" "}
                                        <b>primer envío</b> fue ese mes. El número junto al mes es el{" "}
                                        <b>tamaño</b> de la cohorte.
                                    </li>
                                    <li>
                                        <b>Columnas M0…Mn</b> = meses desde el primer envío. <b>M0</b> es
                                        el mes inicial, <b>M1</b> el mes siguiente, etc.
                                    </li>
                                    <li>
                                        <b>Cada celda</b> ={" "}
                                        {cohortMode === "percent"
                                            ? "% de esa cohorte que sigue enviando ese mes (M0 siempre 100%)."
                                            : "nº de cuentas de esa cohorte que siguen enviando ese mes (M0 = tamaño)."}{" "}
                                        Más oscuro = más retención.
                                    </li>
                                    <li>
                                        Usa el toggle <b>%/#</b> para alternar entre porcentaje y número
                                        absoluto.
                                    </li>
                                </ul>
                            </div>
                            <div className="card" style={cohortCardStyle}>
                                {cohortRows.length === 0 ? (
                                    <div className="ci-empty">No cohorts in range.</div>
                                ) : (
                                    <CohortTriangle
                                        cohorts={cohortRows}
                                        months={data.cohorts.monthsTracked}
                                        mode={cohortMode}
                                    />
                                )}
                            </div>
                        </section>

                        <FrequencyTiersSection tiers={data.frequencyTiers} />

                        <RfmSegmentsSection segments={data.rfm} />

                        <AudienceExportSection
                            baseFilters={analysisFilters}
                            workspaces={availableClients}
                            rfmSegments={data.rfm.map((segment) => segment.segment)}
                            onExport={exportAudience}
                            onSave={saveAudience}
                            onClear={clearAudience}
                            ready={audienceReady}
                            loading={audienceLoading}
                            error={audienceError}
                            count={audienceCount}
                        />

                        <ValueConcentrationSection concentration={data.valueConcentration} />

                        {contacts !== null ? <CounterpartiesSection data={contacts} /> : null}

                        <LifecycleSection months={data.lifecycle} />
                    </>
                )}
            </div>
        </Shell>
    );
}

const cohortCardStyle: CSSProperties = { padding: 16, overflowX: "auto" };
